import {
  Directive,
  ElementRef,
  HostListener,
  afterNextRender,
  inject,
} from '@angular/core';
import {
  NgpPopoverTrigger,
  injectPopoverTriggerState,
  providePopoverConfig,
} from 'ng-primitives/popover';

import { resolveSuiBoundary } from '@sensika/sui/shared';

/**
 * `[suiPopoverTrigger]` — turns an element into a popover trigger, pointing at an
 * `<ng-template>` whose root element carries `[suiPopover]`. Thin wrapper over
 * ng-primitives' `NgpPopoverTrigger`: passes the engine inputs straight through
 * (no transforms — §12 ② of the CRAFTING-GUIDE) and *adds* an imperative surface
 * the call sites use (`isOpen()`, `open()`/`close()`/`toggle()`).
 *
 * `exportAs: 'suiPopoverTrigger'` so a template ref (`#p="suiPopoverTrigger"`) can
 * read `.isOpen()` (e.g. to drive the trigger's `[expanded]` state) and close it
 * imperatively.
 *
 * **Popover ≠ menu.** It is a *stay-open control panel*: inner clicks never close
 * it (there is no item-click-close), focus is the panel's via `[suiPopover]`'s
 * focus-trap (not roving), and it closes only on outside-click / Esc — adjust the
 * close paths with `[suiPopoverCloseOnOutsideClick]` / `[suiPopoverCloseOnEscape]`.
 *
 * Placement uses floating-ui's native 12-point vocab (`bottom-start` default,
 * `top`/`bottom`/`left`/`right` + `-start`/`-end`).
 *
 * Defaults set once via `providePopoverConfig` on this element's injector (which
 * `NgpPopoverTrigger`, a host directive on the same element, reads as its input
 * defaults — sidesteps the §12 ② "can't transform a hostDirective input" trap):
 *  - `placement: 'bottom-start'` — the primitive's own default is `'top'`, wrong
 *    for a panel that opens downward from its trigger.
 *  - `scrollBehavior: 'reposition'` — keep the popover pinned to the trigger on
 *    scroll instead of locking the page.
 *
 * `(openChange)` is the primitive's own output, forwarded verbatim (unlike
 * `NgpMenuTrigger`, which had none and needed an `effect` bridge).
 *
 * Container defaults to the nearest `data-sui-popover-boundary` pane (else
 * `body`), resolved from the trigger's own DOM position, so in-dialog popovers
 * stack correctly with no wiring. `[suiPopoverContainer]` overrides: a template
 * binding makes the engine input controlled and our programmatic `set()` a
 * silent no-op (ng-primitives controlled-input semantics).
 */
@Directive({
  selector: '[suiPopoverTrigger]',
  standalone: true,
  exportAs: 'suiPopoverTrigger',
  hostDirectives: [
    {
      directive: NgpPopoverTrigger,
      inputs: [
        'ngpPopoverTrigger: suiPopoverTrigger',
        'ngpPopoverTriggerPlacement: suiPopoverPlacement',
        'ngpPopoverTriggerDisabled: suiPopoverTriggerDisabled',
        'ngpPopoverTriggerContainer: suiPopoverContainer',
        'ngpPopoverTriggerOffset: suiPopoverOffset',
        'ngpPopoverTriggerCloseOnOutsideClick: suiPopoverCloseOnOutsideClick',
        'ngpPopoverTriggerCloseOnEscape: suiPopoverCloseOnEscape',
        // Position the popover against a *different* element than the clickable
        // trigger (e.g. an icon button inside a field anchors the panel to the
        // whole field). Plain HTMLElement passthrough — no transform.
        'ngpPopoverTriggerAnchor: suiPopoverAnchor',
      ],
      outputs: ['ngpPopoverTriggerOpenChange: openChange'],
    },
  ],
  providers: [
    providePopoverConfig({
      placement: 'bottom-start',
      scrollBehavior: 'reposition',
    }),
  ],
})
export class SuiPopoverTriggerDirective {
  private readonly trigger = inject(NgpPopoverTrigger);
  private readonly state = injectPopoverTriggerState();
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    afterNextRender(() => this.applyAutoContainer());
  }

  /**
   * Re-resolved on every open intent (not just once) because the trigger may be
   * portaled after `afterNextRender` (e.g. inside another panel). Repeated
   * `set()` is harmless: a no-op when `[suiPopoverContainer]` is bound, and the
   * engine snapshots the container at first open anyway.
   */
  @HostListener('pointerdown')
  @HostListener('keydown')
  protected applyAutoContainer(): void {
    this.state().container.set(resolveSuiBoundary(this.host.nativeElement));
  }

  /** Current open state (the primitive exposes `open` as a signal, not a method). */
  isOpen(): boolean {
    return this.trigger.open();
  }

  open(): void {
    if (!this.trigger.open()) {
      this.applyAutoContainer();
      this.trigger.show();
    }
  }

  close(): void {
    if (this.trigger.open()) {
      this.trigger.hide();
    }
  }

  toggle(): void {
    if (this.trigger.open()) {
      this.trigger.hide();
    } else {
      this.applyAutoContainer();
      this.trigger.show();
    }
  }
}
